import type { MergedServiceCodes, ServiceCode } from '@/shared/types';

export interface Permit<T extends ServiceCode = ServiceCode> {
  id: string;
  type: T;
  expiredAt: string;
  issuedAt: string;
  number: string;
  authority: string;
}

type Weapon_Base = {
  id: string;
  NR: string;
  DV: string;
  DP?: string;
  DO?: string;
  OVD?: string;
  ORSR?: string;
  ORNM: string;
  ORVD: string;
  ORKL: string;
  ORST: string;
  NZ: string;
  verified: boolean;
  EXTRA?: {
    purchaseOperation?: {
      lastOperationDate: string;
      secondsLeft?: number;
      nextOperationDeadline?: string;
    };
    KindId?: string;
    ClassifierId?: string;
    Permit?:
      | [
          (
            | Permit<MergedServiceCodes['PURCHASE']>
            | Permit<MergedServiceCodes['STORAGE_CARRYING']>
            | Permit<MergedServiceCodes['STORAGE_CARRYING']>
            | Permit<MergedServiceCodes['COLD_STEEL_STORAGE_FOR_COLLECTION']>
          ),
          Permit<MergedServiceCodes['TEMPORARY_RESIDENCE_RELOCATION']>,
        ]
      | [];
  };
  storageAddress?: string;
  objectMode: WeaponMode;
};

export type Weapon =
  | (Weapon_Base & {
      weaponType: 'firearm_part';
      parts?: Nullable<Array<{ type: string; caliber: string; serialNum: string }>>;
    })
  | (Weapon_Base & {
      weaponType: 'firearm' | 'cold_steel';
      ORMD: string;
      ORIZ: string;
      parts?: Nullable<Array<{ model: string; type: string; caliber: string; serialNum: string }>>;
    });

export enum WeaponMode {
  PERMITTED = 'PERMITTED',
  COMMISSION_SALE = 'COMMISSION_SALE',
  REPAIR = 'REPAIR',
}

export type WeaponStatuses =
  | WeaponMode
  | 'MIN_AGE_18'
  | 'MIN_AGE_21'
  | 'MIN_AGE_25'
  | 'HAS_NO_STORAGE_CARRYING'
  | 'HAS_TIME_OUT';

export type WeaponInformers = 'MIN_AGE_18' | 'HAS_NO_STORAGE_CARRYING' | 'HAS_TIME_OUT';

export type WeaponType = 'firearm' | 'firearm_part' | 'cold_steel';

type Weapon_v2_Base = {
  id: string;
  typeName: string;
  serialNumber: Nullable<string>;
  permitNumber: Nullable<string>;
  permitExpiredAt: Nullable<string>;
  daysLeftForPurchaseOperation: Nullable<number>;
  daysLeftForPermitExpiration: Nullable<number>;
  statuses: WeaponStatuses[];
  extra: {
    permitInfo: {
      permitIssueAt: Nullable<string>;
      permitIssuer: Nullable<string>;
    };
    weaponInfo: {
      vendor: string;
      typeName: string;
      addressStorage: Nullable<string>;
      lastInspectionDate: Nullable<string>;
      totalLength: Nullable<string>;
      weight: Nullable<string>;
      purpose: Nullable<string>;
      category: Nullable<string>;
      numberOfBarrels: Nullable<string>;
      caliber: Nullable<string>;
      model: Nullable<string>;
    };
  };
};

type WeaponPart_Firearm = {
  type: string;
  caliber: Nullable<string>;
  brandName: Nullable<string>;
  serialNum: Nullable<string>;
  vendor?: Nullable<string>;
};

export type Weapon_v2 =
  | (Omit<Weapon_v2_Base, 'extra'> & {
      weaponType: 'firearm_part';
      extra: {
        permitInfo: {
          permitIssueAt: Nullable<string>;
          permitIssuer: Nullable<string>;
        };
      };
    })
  | (Weapon_v2_Base & {
      weaponType: 'firearm';
      model: string;
      parts?: WeaponPart_Firearm[];
    })
  | (Weapon_v2_Base & {
      weaponType: 'cold_steel';
      model: string;
    });

export interface ResponseUserWeapons_v2 {
  informers: WeaponInformers[];
  weapons: Weapon_v2[];
}

export type CriminalWeapon =
  | (Weapon_Base & {
      weaponType: 'firearm_part';
      ORNZ?: string;
      parts?: Nullable<Array<{ type: string; caliber: string; serialNum: string }>>;
    })
  | (Weapon_Base & {
      weaponType: 'firearm' | 'cold_steel';
      ORMD: string;
      ORIZ: string;
      ORNZ?: string;
      parts?: Nullable<Array<{ model: string; type: string; caliber: string; serialNum: string }>>;
    });

export interface ResponseUserWeapons {
  ipsItems: Weapon[];
  criminalItems: CriminalWeapon[];
}

export type InspectionType = Exclude<Weapon['weaponType'], 'cold_steel'>;

export interface ERZPermit {
  id: string;
  permitType: ServiceCode;
  num: string;
  issuedAt: string;
  expiredAt: string;
  canExtract: boolean;
  objects:
    | []
    | [
        {
          id: Weapon['id'];
          type: Nullable<InspectionType>;
        },
      ];
}

export interface PurchaseItem {
  id: PurchaseItemIdMapping;
  name: string;
}

export enum PurchaseItemIdMapping {
  BLANK_WEAPON = 'dbaebc49-5e1a-4454-8288-0942de17acbb',
  GAS_REVOLVERS = 'f95a799e-4bd4-4db8-ad21-8e15a4e53bc8',
  HUNTING_SMOOTHBORE_WEAPONS = '2c3886ec-bfa1-4c15-b4b3-2be63e94301e',
  PNEUMATIC_WEAPONS = '3fc24a98-df37-4fdc-8ea3-df4195ed1da9',
  COLD_WEAPON = '5b0846a3-47e9-4248-87c9-56ad5c90e679',
  WEAPONS_MAIN_PART = 'd6c1dd07-0913-44f1-af52-ee54b5333b48',
  HUNTING_RIFLED_WEAPONS = '57a35547-da40-4596-82ab-747cb6f0b8a7',
  DEVICE = 'ad898d08-eb2e-48d4-9712-bb8f4638314a',
}

export type ServiceSupportedCustomWeaponList =
  | MergedServiceCodes['RESIDENCE_RELOCATION']
  | MergedServiceCodes['TEMPORARY_RESIDENCE_RELOCATION']
  | MergedServiceCodes['STORAGE_CARRYING']
  | MergedServiceCodes['COMMISSION_SALE']
  | MergedServiceCodes['COLD_STEEL_STORAGE_FOR_COLLECTION']
  | MergedServiceCodes['COLD_STEEL_TRANSPORTATION']
  | MergedServiceCodes['TRANSPORT_ACROSS_BORDER']
  | MergedServiceCodes['PURCHASE'];

export interface ResponseUserWeaponTechnicalConditionDocument {
  id: string;
  file: Nullable<string>;
  issued: Nullable<string>;
  issued_by: Nullable<string>;
  number: Nullable<string>;
}

export type GetWeaponTechnicalConditionDocumentParams = {
  id: string;
  type: InspectionType;
};
